/**
 * Pure logic behind the Publish drawer. No React, no fetch: tested in publishPackage.test.ts.
 */
import type { PackageCheck, RegisterReleaseBody } from '../api/releases';

/** Largest package the API accepts on /check. */
export const MAX_PACKAGE_BYTES = 100 * 1024 * 1024;

/** Why a picked file cannot be uploaded at all, or null when it can go to /check. */
export function packageFileProblem(file: { name: string; size: number }): string | null {
  if (!/\.sppkg$/i.test(file.name)) return 'That is not a .sppkg file.';
  if (file.size > MAX_PACKAGE_BYTES) return 'The package is larger than 100 MB.';
  return null;
}

/**
 * True when the package cannot be published. A check that failed early (size, zip, manifest)
 * returns no facts and may omit the version and sha256, so either missing also blocks publishing.
 */
export function checkFailed(result: PackageCheck): boolean {
  return !result.ok || result.checks.some((c) => !c.ok) || !result.version || !checkedSha256(result);
}

/** The sha256 register must send: the top-level one /check returns, else the one in its facts. */
export function checkedSha256(result: PackageCheck): string | null {
  const top = typeof result.sha256 === 'string' && result.sha256 ? result.sha256 : null;
  return top ?? (result.facts && typeof result.facts.sha256 === 'string' && result.facts.sha256 ? result.facts.sha256 : null);
}

export interface PackageFacts { sizeBytes: number | null; sha256Prefix: string | null }

/** The size and hash line, tolerating a result without facts. */
export function readPackageFacts(result: PackageCheck, fallbackSizeBytes?: number): PackageFacts {
  const f = result.facts;
  const size = f && typeof f.sizeBytes === 'number' ? f.sizeBytes : fallbackSizeBytes;
  return {
    sizeBytes: typeof size === 'number' && isFinite(size) ? size : null,
    sha256Prefix: checkedSha256(result)?.slice(0, 12) ?? null,
  };
}

/**
 * The body POSTed to register a checked package: { version, sha256, fileName?, notes, makeCurrent? }.
 * The one place the register contract is built. makeCurrent is sent only as literal true; a draft
 * omits it. Null when the check gave no version or sha256 to register.
 */
export function buildRegisterBody(result: PackageCheck, notes: string, makeCurrent: boolean): RegisterReleaseBody | null {
  const sha256 = checkedSha256(result);
  if (!result.version || !sha256) return null;
  const body: RegisterReleaseBody = { version: result.version, sha256, notes: notes.trim() };
  if (result.fileName) body.fileName = result.fileName;
  if (makeCurrent) body.makeCurrent = true;
  return body;
}
